import React, { useState, useMemo, useRef, useEffect } from 'react';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/dist/styles/ag-grid.css';
import 'ag-grid-community/dist/styles/ag-theme-alpine.css';
import classNames from 'classnames';

import style from './style.module.css';

const RED = '#ffb3b3';
const GREEN = '#cef9ce';

const ClosedDealsFilter = ({ value, onChange }) => (
  <div className="form-group">
    <label htmlFor="closeDealsFilter">Closed Deals</label>
    <input
      className="form-control"
      id="closeDealsFilter"
      min={1}
      onChange={event => onChange(Number(event.target.value))}
      type="number"
      value={value}
    />
  </div>
);

const ListsSentFilter = ({ value, onChange }) => (
  <div className="form-group">
    <label htmlFor="listsSentFilter">Approval Lists Sent</label>
    <input
      className="form-control"
      id="listsSentFilter"
      min={1}
      onChange={event => onChange(Number(event.target.value))}
      type="number"
      value={value}
    />
  </div>
);

const MyAgGridGrouped = () => {
  const [closedDeals, setClosedDeals] = useState(20);
  const [listsSent, setListsSent] = useState(50);
  const gridRef = useRef(null);

  const handleClosedDealsChange = value => {
    setClosedDeals(value);
  };

  const handleListsSentChange = value => {
    setListsSent(value);
  };

  useEffect(() => {
    if (gridRef.current.api) {
      gridRef.current.api.redrawRows();
    }
  }, [gridRef, closedDeals, listsSent]);

  const columnDefs = useMemo(
    () => [
      {
        children: [
          {
            headerName: 'Contractual Client (dba/search)',
            field: 'fund',
            pinned: 'left',
          },
          { headerName: 'Module', field: 'module' },
          { headerName: 'DM1', field: 'dm1' },
          { headerName: 'DM2', field: 'dm2' },
          { headerName: 'Type', field: 'type' },
          {
            headerName: 'Current Offer or Better?',
            field: 'currentOffer',
            cellStyle: params => (params.value === 'No' ? { backgroundColor: RED } : { backgroundColor: GREEN }),
          },
          {
            field: 'closedDeals',
            cellStyle: params => {
              const { closedDeals } = params.context;

              return params.value > closedDeals ? { backgroundColor: RED } : undefined;
            },
          },
        ],
      },
      {
        headerName: 'Retainer Length',
        headerClass: style.retainerHeaderCell,
        children: [
          { headerName: 'Retainer Start Date', field: 'retainerStart' },
          { headerName: 'Months as Client', field: 'monthsAsClient', cellClass: style.greyCell },
          { headerName: 'Years as Client', field: 'yearsAsClient', cellClass: style.greyCell },
        ],
      },
      {
        headerName: 'Approval List Statistics',
        headerClass: style.approvalListStatisticsHeaderCell,
        children: [
          {
            headerName: 'Approval Lists Sent',
            field: 'listsSent',
            cellStyle: params => {
              const { listsSent } = params.context;

              return params.value > listsSent ? { backgroundColor: RED } : undefined;
            },
          },
          { headerName: 'Approval Lists per Month', field: 'listsPerMonth' },
          {
            headerName: 'Date of last sent approval list',
            field: 'lastListDate',
          },
          {
            headerName: 'Days since last approval list',
            field: 'daysSinceLast',
            cellStyle: params => (params.value > 300 ? { backgroundColor: RED } : undefined),
          },
          { headerName: 'Targets Sent', headerClass: style.brownHeaderCell, field: 'targetsSent' },
        ],
      },
    ],
    [],
  );

  const [rowData] = useState([
    {
      fund: 'Bain Capital',
      module: 'Hartley',
      dm1: 'bloewenstein',
      dm2: '',
      type: 'Add-on',
      currentOffer: 'No',
      closedDeals: 48,
      retainerStart: '2/1/2016',
      monthsAsClient: 116,
      yearsAsClient: 9.7,
      listsSent: 16,
      listsPerMonth: 0.14,
      lastListDate: '5/22/2024',
      daysSinceLast: 460,
      targetsSent: 1254,
    },
    {
      fund: 'CI Capital Partners, LLC',
      module: 'Hartley',
      dm1: 'tbailleul',
      dm2: '',
      type: 'Add-on',
      currentOffer: 'Yes',
      closedDeals: 15,
      retainerStart: '5/8/2020',
      monthsAsClient: 65,
      yearsAsClient: 5.4,
      listsSent: 25,
      listsPerMonth: 0.39,
      lastListDate: '10/8/2024',
      daysSinceLast: 260,
      targetsSent: 2321,
    },
    {
      fund: 'Dytech Automotive Group (GreatWater Garages)',
      module: 'Friedman',
      dm1: 'kvoung',
      dm2: '',
      type: 'Add-on',
      currentOffer: 'No',
      closedDeals: 11,
      retainerStart: '12/8/2021',
      monthsAsClient: 72,
      yearsAsClient: 5.1,
      listsSent: 80,
      listsPerMonth: 0.59,
      lastListDate: '11/8/2024',
      daysSinceLast: 301,
      targetsSent: 422,
    },
  ]);

  return (
    <div className="container p20">
      <div className="panel panel-primary">
        <div className="panel-heading">Filter</div>
        <div className="panel-body p15">
          <div className="row">
            <div className="col-sm-2">
              <ClosedDealsFilter onChange={handleClosedDealsChange} value={closedDeals} />
            </div>
            <div className="col-sm-2">
              <ListsSentFilter onChange={handleListsSentChange} value={listsSent} />
            </div>
          </div>
        </div>
      </div>

      <div className={classNames(style.grid, 'ag-theme-alpine')} style={{ height: 600, width: '100%' }}>
        <AgGridReact
          ref={gridRef}
          columnDefs={columnDefs}
          context={{ closedDeals, listsSent }}
          defaultColDef={{
            sortable: true,
            filter: true,
            resizable: true,
          }}
          domLayout="autoHeight"
          headerHeight={24}
          rowData={rowData}
          rowHeight={24}
          suppressMovableColumns
          suppressRowHoverHighlight
        />
      </div>
    </div>
  );
};

export default MyAgGridGrouped;
